import type { VehicleFilters } from "@/lib/filters/types.generated";
import type { SortOption } from "@/app/(app)/[bookingType]/search/[searchId]/vehicleComponents/VehicleFilterBar";
import type {
  VehicleBookingRequest,
  VehicleSummary,
} from "@/generated/search.openapi";
import { parseRateNumeric } from "@/lib/filters/vehicle-filters.generated";

export interface CorporateDiscount {
  vendorCode: string;
  vendorName: string;
  vendorCodes?: string[];
  code: string;
}
export interface VehicleSearchContext {
  request: Record<string, unknown>;
  resultsPath: string;
  discount?: CorporateDiscount;
  corporateOnly?: boolean;
  vehicleFilters?: VehicleFilters;
  showAaa?: boolean;
  sortBy?: SortOption;
}
const PREFIX = "gondola:vehicle-search:";
const OWNER = `${PREFIX}owner`;
const intents = new Map<string, symbol>();
export function beginVehicleSearchIntent(source: string): () => boolean {
  const generation = Symbol(source);
  intents.set(source, generation);
  return () => intents.get(source) === generation;
}

// Tab-scoped with an in-memory fallback for browsers that disable storage.
// The session boundary synchronizes ownership before rendering any consumers.
let memoryOwner: string | null | undefined;
const ownerListeners = new Set<() => void>();
export const getVehicleSearchOwner = () => memoryOwner;
export function subscribeVehicleSearchOwner(listener: () => void) {
  ownerListeners.add(listener);
  return () => {
    ownerListeners.delete(listener);
  };
}
const memoryContexts = new Map<string, VehicleSearchContext>();
const memoryBookings = new Map<string, VehicleBookingRequest>();
export function setVehicleSearchOwner(owner: string | null): void {
  if (memoryOwner !== owner || owner === null) {
    memoryContexts.clear();
    memoryBookings.clear();
    intents.clear();
    memoryOwner = owner;
    // Ownership is synchronized during the session boundary render. Notify
    // mounted initial-search drafts after that render, never update siblings
    // from inside another component's render.
    queueMicrotask(() => ownerListeners.forEach((listener) => listener()));
  }
  try {
    if (sessionStorage.getItem(OWNER) !== owner) {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith(PREFIX))
        .forEach((key) => sessionStorage.removeItem(key));
    }
    if (owner) sessionStorage.setItem(OWNER, owner);
  } catch {
    /* Memory still preserves context until this tab closes. */
  }
}
export function readVehicleSearchContext(
  id: string
): VehicleSearchContext | undefined {
  try {
    const value = sessionStorage.getItem(`${PREFIX}${id}`);
    if (value) return JSON.parse(value);
  } catch {
    /* Use the session-scoped memory fallback. */
  }
  return memoryContexts.get(id);
}
export function saveVehicleSearchContext(
  id: string,
  context: VehicleSearchContext
): void {
  memoryContexts.set(id, context);
  try {
    sessionStorage.setItem(`${PREFIX}${id}`, JSON.stringify(context));
  } catch {
    /* Memory fallback above remains available. */
  }
}
export function sanitizeVehicleSearchParams(
  params: URLSearchParams
): URLSearchParams {
  const clean = new URLSearchParams(params);
  ["discountCode", "discountVendor", "discountVendorName"].forEach((key) =>
    clean.delete(key)
  );
  return clean;
}
export function vehicleResultsPath(id: string): string {
  return (
    readVehicleSearchContext(id)?.resultsPath ??
    `/car/search/${encodeURIComponent(id)}`
  );
}
export function selectCorporateOffer(
  car: VehicleSummary
): VehicleSummary | null {
  const corporate = car.rates
    ?.filter((rate) => rate.rateType === "corporate")
    .sort(
      (a, b) => parseRateNumeric(a.totalRate) - parseRateNumeric(b.totalRate)
    )[0];
  if (!corporate) return null;
  return {
    ...car,
    ...corporate,
    rateCode: corporate.rateCode ?? car.rateCode,
    rates: [corporate],
    discountApplied: true,
  };
}

export function readVehicleBookingAttempt(
  searchId: string
): VehicleBookingRequest | undefined {
  try {
    const saved = sessionStorage.getItem(`${PREFIX}booking:${searchId}`);
    if (saved) return JSON.parse(saved);
  } catch {
    /* Session memory remains available. */
  }
  return memoryBookings.get(searchId);
}
export function saveVehicleBookingAttempt(
  searchId: string,
  request?: VehicleBookingRequest
): void {
  if (request) memoryBookings.set(searchId, request);
  else memoryBookings.delete(searchId);
  try {
    if (request)
      sessionStorage.setItem(
        `${PREFIX}booking:${searchId}`,
        JSON.stringify(request)
      );
    else sessionStorage.removeItem(`${PREFIX}booking:${searchId}`);
  } catch {
    /* Session memory remains available. */
  }
}
